import React, { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import './index.css';
import { primeRuntimePortals } from './config/runtimePortals';

// Initialize theme on first load
(() => {
  try {
    const root = document.documentElement;
    const policy = localStorage.getItem('themePolicy'); // 'system' | 'light' | 'dark'
    const saved = localStorage.getItem('theme');
    const systemPrefersDark = window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches;
    // Admin policy overrides user preference
    if (policy === 'dark') {
      root.classList.add('dark');
    } else if (policy === 'light') {
      root.classList.remove('dark');
    } else if (saved === 'dark' || (!saved && systemPrefersDark)) {
      root.classList.add('dark');
    } else {
      root.classList.remove('dark');
    }
  } catch {}
})();

// Global fetch wrapper to ensure Authorization header for /api calls
(() => {
  try {
    const originalFetch = window.fetch.bind(window);
    window.fetch = (input: RequestInfo | URL, init?: RequestInit) => {
      try {
        const urlStr =
          typeof input === 'string'
            ? input
            : input instanceof URL
              ? input.toString()
              : input instanceof Request
                ? input.url
                : String(input);

        const isApi = urlStr.startsWith('/api') || urlStr.startsWith(window.location.origin + '/api');
        if (!isApi) return originalFetch(input as any, init);

        // Public bootstrap/data endpoints must stay anonymous. Sending an old
        // bearer token here makes permit-all requests pass through JWT/session
        // restoration and can block startup when authentication is unhealthy.
        const parsedUrl = new URL(urlStr, window.location.origin);
        const isAnonymousApi = parsedUrl.pathname === '/api/config/portal'
          || parsedUrl.pathname.startsWith('/api/public/');
        if (isAnonymousApi) return originalFetch(input as any, init);

        const token = localStorage.getItem('auth_token') || localStorage.getItem('accessToken') || localStorage.getItem('authToken');
        if (!token) return originalFetch(input as any, init);

        // Merge headers and add Authorization if missing
        const headers = new Headers((init && init.headers) || (input instanceof Request ? input.headers : undefined));
        if (!headers.has('Authorization')) {
          headers.set('Authorization', `Bearer ${token}`);
        }

        const method = (init?.method || (input instanceof Request ? input.method : 'GET') || 'GET').toUpperCase();
        const body: any = init?.body;
        const isFormData =
          !!body &&
          typeof FormData !== 'undefined' &&
          (body instanceof FormData || Object.prototype.toString.call(body) === '[object FormData]');

        // If sending FormData, ensure we do NOT carry a JSON Content-Type.
        // Browser must set multipart boundary automatically.
        if (isFormData && headers.has('Content-Type')) headers.delete('Content-Type');

        // If input is a Request, we can't reliably see the body; strip Content-Type for known upload endpoint anyway.
        try {
          const parsed = new URL(urlStr, window.location.origin);
          const path = parsed.pathname;
          const isTaskUpload = /\/api\/documents\/task\/[^/]+\/upload$/i.test(path);
          if (method === 'POST' && isTaskUpload && headers.has('Content-Type')) headers.delete('Content-Type');
        } catch {}

        const nextInit: RequestInit = { ...(init || {}), headers };
        return originalFetch(input as any, nextInit);
      } catch {
        return originalFetch(input as any, init);
      }
    };
  } catch {}
})();

// Defensive cleanup for older client-side caches/service workers.
// This helps if a prior build registered a worker that is still serving stale UI chunks.
// Robust handling for restricted/webview contexts where service workers may not be available.
(() => {
  try {
    // Only attempt cleanup if in a fully-featured browsing context
    // Skip in sandboxed/restricted contexts (webviews, iframes with restrictions, etc.)
    const isRestrictedContext = typeof navigator === 'undefined' || !navigator;
    
    if (!isRestrictedContext && 'serviceWorker' in navigator) {
      try {
        Promise.resolve()
          .then(() => navigator.serviceWorker.getRegistrations?.())
          .then((registrations) => {
            if (registrations && Array.isArray(registrations)) {
              return Promise.allSettled(registrations.map((reg) => {
                try {
                  return reg.unregister();
                } catch {
                  return Promise.reject();
                }
              }));
            }
          })
          .catch(() => {/* Silently ignore - may be in restricted context */});
      } catch (err) {
        // Service worker API may throw in restricted contexts
      }
    }

    if ('caches' in window) {
      try {
        Promise.resolve()
          .then(() => caches.keys?.())
          .then((keys) => {
            if (keys && Array.isArray(keys)) {
              return Promise.allSettled(keys.map((key) => {
                try {
                  return caches.delete(key);
                } catch {
                  return Promise.reject();
                }
              }));
            }
          })
          .catch(() => {/* Silently ignore - may be in restricted context */});
      } catch (err) {
        // Cache API may throw in restricted contexts
      }
    }
  } catch {
    // Silently ignore any top-level errors
  }
})();

(async () => {
  try {
    // Prime the local portal list before authentication chooses a workspace.
    // loadPortalConfig has its own short timeout and never rejects, so this
    // cannot leave the root unmounted.
    await primeRuntimePortals();
  } catch {}

  const [{ default: App }, { ToastProvider }] = await Promise.all([
    import('./App'),
    import('./components/common/ToastProvider'),
  ]);

  createRoot(document.getElementById('root')!).render(
    <StrictMode>
      <ToastProvider>
        <App />
      </ToastProvider>
    </StrictMode>
  );
})();
